import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../Components/Header';
import Footer from '../Components/Footer';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Marvel Sato | Blogs";
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/blog/blogs`); // Update with your URL
        setBlogPosts(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error loading blogs", error);
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  if (loading) return <div className="h-screen flex items-center justify-center text-orange-500 font-bold uppercase tracking-widest">Loading Insights...</div>;

  return (
    <div className="bg-white overflow-x-hidden">
      <Header />

      {/* --- HERO SECTION --- */}
      <section className="relative h-[45vh] flex items-center justify-center bg-slate-900 pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-orange-500/10 mix-blend-overlay"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white"></div>
        </div>
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center px-6"
        >
          <h1 className="text-4xl md:text-7xl font-black text-slate-900 tracking-tighter italic uppercase">
            Marvel <span className="text-orange-500 not-italic">Insights</span>
          </h1>
          <p className="text-slate-500 mt-4 font-bold uppercase tracking-[0.2em] text-xs md:text-sm">Explore our world through our blogs</p>
        </motion.div>
      </section>

      {/* --- INTRO SECTION --- */}
      <section className="py-12 px-6 md:px-24 max-w-5xl mx-auto text-center">
        <motion.div {...fadeInUp}>
          <p className="text-slate-600 text-lg md:text-xl leading-relaxed font-medium">
            Curious about elevator specialists, components, systems, manufacturing and design? 
            Here, we provide the latest updates on all kinds of elevator solutions. 
            Our blog collection lets you explore the latest innovations and <span className="text-orange-500 italic">lift expectations</span>.
          </p>
          <div className="h-1 w-20 bg-orange-500 mx-auto mt-8"></div>
          <p className="text-slate-500 text-sm md:text-md mt-8 leading-relaxed italic">
            "Read more about the lifting equipment and how we have become the central hub for industrial activities. 
            We directly take you to a detailed range of solutions that covers more about the features, 
            benefits and engineering of equipment."
          </p>
        </motion.div>
      </section>

      {/* --- BLOG GRID --- */}
      <section className="py-16 px-6 md:px-24 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {blogPosts.map((post, index) => (
            <motion.div
              key={post._id}
              {...fadeInUp}
              transition={{ delay: index * 0.1 }}
              className="group flex flex-col bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500"
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                />
                <div className="absolute top-4 left-4 bg-orange-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase italic">
                  Marvel Sato
                </div>
              </div>

              <div className="p-8 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-slate-400 text-xs mb-4 font-bold uppercase">
                  <span className="flex items-center gap-1">
                    <Calendar size={14}/> {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1"><User size={14}/> {post.author}</span>
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-4 leading-tight group-hover:text-orange-500 transition-colors uppercase">
                  {post.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-6 line-clamp-3">
                  {post.excerpt}
                </p>
                <Link  
                  to={`/blog/${post._id}`} // We use imageId for the URL now  
                  className="mt-auto flex items-center gap-2 text-slate-900 font-black uppercase text-xs tracking-tighter italic border-b-2 border-orange-500 w-fit pb-1 hover:gap-4 transition-all"
                >
                  Read Full Article <ArrowRight size={16} className="text-orange-500" />
                </Link> 
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;